<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetRegisterEntry',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'documentTypeChanged']);

  const props = defineProps({
    item: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
    form: {
      type: Object,
      required: false,
    },
    isSaving: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const onReferenceEntrySelected = (...args) => {
    const [option] = args;

    if (props.item.id == null && option.invoice_type === 'proforma') {
      props.item.invoice.amount = option.amount;
      props.item.invoice.fulfillment_date = option.fulfillment_date;
      props.item.invoice.due_date = option.due_date;
      props.item.partner_id = option.client_id;
    }
  };
  const onReferenceNumberChanged = (...args) => {
    const [referenceNumber] = args;

    if (!referenceNumber || !referenceNumber.trim().length) {
      return;
    }

    const item = props.item;

    if (
      item.document_type == 'invoice' &&
      item.isNew &&
      (!item.invoice || !item.invoice.id)
    ) {
      checkInvoiceExists(referenceNumber.trim());
    }
  };
  const checkInvoiceExists = (...args) => {
    const [invoiceNumber] = args;
    $fn.value
      .fetch('check', {
        parameters: {
          kind: 'invoice',
          op: 'exists',
          query: invoiceNumber,
          company_id: props.item.version.company_id,
          partner_id: _.get(props.item.partner, 'id'),
        },
      })
      .then((resp) => {
        if (resp.passed) {
          invoiceCheckResult.value = resp.data;
          return;
        }

        invoiceCheckResult.value = null;
      });
  };
  const assignInvoiceCheckResult = (...args) => {
    if (!invoiceCheckResult.value) {
      return;
    }

    const item = props.item;

    item.invoice = invoiceCheckResult.value;

    if (!item.partner || !item.partner.id) {
      item.partner =
        invoiceCheckResult.value.partner || invoiceCheckResult.value.client;
    }

    invoiceCheckResult.value = null;
  };

  const priorities = ref({
    0: $fn.value.pgettext('Priority', 'normal'),
    1: $fn.value.pgettext('Priority', 'important'),
    2: $fn.value.pgettext('Priority', 'urgent'),
  });
  const documentTypes = ref(null);
  const directions = ref(null);
  const invoiceTypes = ref(null);
  const paymentMethods = ref(null);
  const tags = ref(null);
  const categories = ref(null);
  const statuses = ref(null);
  const clerks = ref(null);
  const deliveryModes = ref(null);
  const currencies = ref(['HUF', 'EUR', 'USD']);
  const invoiceCheckResult = ref(null);
  const ux = ref([]);
  const b_8680084583475136_dataSource = computed(() => ({
    name: 'suggest_register_entry',
    parameters: {
      register_id: props.item.version.register_id,
      source: props.item.id,
    },
  }));

  const $data = computed(() => ({
    priorities: priorities.value,
    documentTypes: documentTypes.value,
    directions: directions.value,
    invoiceTypes: invoiceTypes.value,
    paymentMethods: paymentMethods.value,
    tags: tags.value,
    categories: categories.value,
    statuses: statuses.value,
    clerks: clerks.value,
    deliveryModes: deliveryModes.value,
    currencies: currencies.value,
    invoiceCheckResult: invoiceCheckResult.value,
    ux: ux.value,
    b_8680084583475136_dataSource: b_8680084583475136_dataSource.value,
  }));

  const $$load_documentTypes = async () => {
    return $fn.value.getEnumValuesFromSchema('RegisterDocumentType');
  };
  const $$load_directions = async () => {
    return $fn.value.getEnumValuesFromSchema('RegisterEntryDirection');
  };
  const $$load_invoiceTypes = async () => {
    return $fn.value.getEnumValuesFromSchema('InvoiceType');
  };
  const $$load_paymentMethods = async () => {
    return $fn.value.fetch('list_payment_method').then((data) =>
      _.map(data, (p) => ({
        value: p.id,
        text: $fn.value.pgettext('PaymentMethod', p.name),
      }))
    );
  };
  const $$load_tags = async () => {
    return $fn.value.fetch('get_tags', {
      parameters: { tag_type: 'register-entry:*' },
    });
  };
  const $$load_categories = async () => {
    return _.get($data.value.tags, 'register-entry:category', []);
  };
  const $$load_statuses = async () => {
    return _.get($data.value.tags, 'register-entry:status', []);
  };
  const $$load_clerks = async () => {
    return _.get($data.value.tags, 'register-entry:clerk', []);
  };
  const $$load_deliveryModes = async () => {
    return _.get($data.value.tags, 'register-entry:delivery-mode', []);
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'priorities' },
      { name: 'documentTypes' },
      { name: 'directions' },
      { name: 'invoiceTypes' },
      { name: 'paymentMethods' },
      { name: 'tags' },
      { name: 'categories', dependsOn: ['tags'] },
      { name: 'statuses', dependsOn: ['tags'] },
      { name: 'clerks', dependsOn: ['tags'] },
      { name: 'deliveryModes', dependsOn: ['tags'] },
      { name: 'currencies' },
      { name: 'invoiceCheckResult' },
      { name: 'ux' },
      { name: 'b_8680084583475136_dataSource', nodeId: '8680084583475136' },
    ],
    loaders: {
      documentTypes: $$load_documentTypes,
      directions: $$load_directions,
      invoiceTypes: $$load_invoiceTypes,
      paymentMethods: $$load_paymentMethods,
      tags: $$load_tags,
      categories: $$load_categories,
      statuses: $$load_statuses,
      clerks: $$load_clerks,
      deliveryModes: $$load_deliveryModes,
    },
    ux: {},
    dataRefs: {
      priorities,
      documentTypes,
      directions,
      invoiceTypes,
      paymentMethods,
      tags,
      categories,
      statuses,
      clerks,
      deliveryModes,
      currencies,
      invoiceCheckResult,
      ux,
      b_8680084583475136_dataSource,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const invoiceRegister = computed(() => {
    return props.item.document_type === 'invoice';
  });
  const contractRegister = computed(() => {
    return props.item.document_type === 'contract';
  });

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );
  watch(
    () => props.form,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('form');
    }
  );
  watch(
    () => props.isSaving,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('isSaving');
    }
  );

  const act_1468474219929533_emit_ph31a37i6_getActionArgs = async ($event) => {
    return {
      event: 'documentTypeChanged',
    };
  };
  const act_6917646200545441_script_tnz2f9mwz_value_callable = async (
    $event
  ) => {
    return onReferenceNumberChanged($event.originalEvent);
  };
  const act_6917646200545441_script_tnz2f9mwz_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_6917646200545441_script_tnz2f9mwz_value_callable($event);
      },
    };
  };
  const act_6941071772471093_script_630q7cywd_value_callable = async (
    $event
  ) => {
    return assignInvoiceCheckResult();
  };
  const act_6941071772471093_script_630q7cywd_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_6941071772471093_script_630q7cywd_value_callable($event);
      },
    };
  };
  const act_7463771596263925_emit_kjt4tljat_getActionArgs = async ($event) => {
    return {
      event: 'close',
    };
  };
  const act_7463771596263925_showModal_silo42ppr_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateInvoice',
      props: {
        registerEntry: props.item.id,
      },
    };
  };
  const act_8680084583475136_script_pu6ljtyel_value_callable = async (
    $event
  ) => {
    return onReferenceEntrySelected($event.data.option);
  };
  const act_8680084583475136_script_pu6ljtyel_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_8680084583475136_script_pu6ljtyel_value_callable($event);
      },
    };
  };

  const act_7315092382398562_reloadComponentData_b3bqm26op =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_0liofc02s =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_b3bqm26op,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_0liofc02s],
    event: 'reload',
  });
  const act_1468474219929533_emit_ph31a37i6 = new actionTypes.EmitAction({
    actionArgs: act_1468474219929533_emit_ph31a37i6_getActionArgs,
  });
  const inst_evh_1468474219929533_input_82k67l4wb =
    new actionTypes.EventHandlerImpl({
      action: act_1468474219929533_emit_ph31a37i6,
      event: 'input',
      displayName: 'emit',
    });
  const inst_evh_1468474219929533_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1468474219929533_input_82k67l4wb],
    event: 'input',
  });
  const act_6917646200545441_script_tnz2f9mwz = new actionTypes.ScriptAction({
    actionArgs: act_6917646200545441_script_tnz2f9mwz_getActionArgs,
  });
  const inst_evh_6917646200545441_input_sec7mx7b7 =
    new actionTypes.EventHandlerImpl({
      action: act_6917646200545441_script_tnz2f9mwz,
      event: 'input',
      displayName: 'script',
    });
  const inst_evh_6917646200545441_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6917646200545441_input_sec7mx7b7],
    event: 'input',
  });
  const act_6941071772471093_script_630q7cywd = new actionTypes.ScriptAction({
    actionArgs: act_6941071772471093_script_630q7cywd_getActionArgs,
  });
  const inst_evh_6941071772471093_clickPreventStop_oz7sxzehu =
    new actionTypes.EventHandlerImpl({
      action: act_6941071772471093_script_630q7cywd,
      event: 'click.prevent.stop',
      displayName: 'script',
    });
  const inst_evh_6941071772471093_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6941071772471093_clickPreventStop_oz7sxzehu],
      event: 'click.prevent.stop',
    });
  const act_7463771596263925_emit_kjt4tljat = new actionTypes.EmitAction({
    actionArgs: act_7463771596263925_emit_kjt4tljat_getActionArgs,
  });
  const inst_evh_7463771596263925_clickPreventStop_n9nev7pdf =
    new actionTypes.EventHandlerImpl({
      action: act_7463771596263925_emit_kjt4tljat,
      event: 'click.prevent.stop',
      displayName: 'emit',
    });
  const act_7463771596263925_showModal_silo42ppr =
    new actionTypes.ShowModalAction({
      actionArgs: act_7463771596263925_showModal_silo42ppr_getActionArgs,
    });
  const inst_evh_7463771596263925_clickPreventStop_ui5olg7gj =
    new actionTypes.EventHandlerImpl({
      action: act_7463771596263925_showModal_silo42ppr,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_7463771596263925_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_7463771596263925_clickPreventStop_n9nev7pdf,
        inst_evh_7463771596263925_clickPreventStop_ui5olg7gj,
      ],
      event: 'click.prevent.stop',
    });
  const act_8680084583475136_script_pu6ljtyel = new actionTypes.ScriptAction({
    actionArgs: act_8680084583475136_script_pu6ljtyel_getActionArgs,
  });
  const inst_evh_8680084583475136_optionSelected_46nm13e1f =
    new actionTypes.EventHandlerImpl({
      action: act_8680084583475136_script_pu6ljtyel,
      event: 'optionSelected',
      displayName: 'script',
    });
  const inst_evh_8680084583475136_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8680084583475136_optionSelected_46nm13e1f],
      event: 'optionSelected',
    });

  const actions = {
    inst_evh_7315092382398562_reload_0liofc02s,
    inst_evh_7315092382398562_reload,
    inst_evh_1468474219929533_input_82k67l4wb,
    inst_evh_1468474219929533_input,
    inst_evh_6917646200545441_input_sec7mx7b7,
    inst_evh_6917646200545441_input,
    inst_evh_6941071772471093_clickPreventStop_oz7sxzehu,
    inst_evh_6941071772471093_clickPreventStop,
    inst_evh_7463771596263925_clickPreventStop_n9nev7pdf,
    inst_evh_7463771596263925_clickPreventStop_ui5olg7gj,
    inst_evh_7463771596263925_clickPreventStop,
    inst_evh_8680084583475136_optionSelected_46nm13e1f,
    inst_evh_8680084583475136_optionSelected,
  };

  const evh_7315092382398562_reload_0liofc02s = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_0liofc02s.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1468474219929533_input_82k67l4wb = async (event, scope) => {
    return await actions.inst_evh_1468474219929533_input_82k67l4wb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1468474219929533_input = async (event, scope) => {
    return await actions.inst_evh_1468474219929533_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6917646200545441_input_sec7mx7b7 = async (event, scope) => {
    return await actions.inst_evh_6917646200545441_input_sec7mx7b7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6917646200545441_input = async (event, scope) => {
    return await actions.inst_evh_6917646200545441_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6941071772471093_clickPreventStop_oz7sxzehu = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6941071772471093_clickPreventStop_oz7sxzehu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6941071772471093_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_6941071772471093_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7463771596263925_clickPreventStop_n9nev7pdf = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7463771596263925_clickPreventStop_n9nev7pdf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7463771596263925_clickPreventStop_ui5olg7gj = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7463771596263925_clickPreventStop_ui5olg7gj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7463771596263925_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7463771596263925_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8680084583475136_optionSelected_46nm13e1f = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8680084583475136_optionSelected_46nm13e1f.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8680084583475136_optionSelected = async (event, scope) => {
    return await actions.inst_evh_8680084583475136_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpFormGroup
      :label-cols-md="3"
      :content-cols-md="3"
      name="entry_date"
      :label="pgettext('RegisterEntry', 'Date')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormDatetime
        name="entry_date"
        v-model="item.entry_date"
        :readonly="item.isReadonly"
        required
      >
      </ItpFormDatetime>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="3"
      :content-cols-md="9"
      name="document_type"
      :label="pgettext('RegisterEntry', 'Document type')"
      :labelAlign="labelAlign"
      contentClass="row"
      required
    >
      <div class="col-4">
        <ItpFormSelect2
          name="direction"
          v-model="item.direction"
          :value="item.direction"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.direction =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          :options="directions"
          valueField="value"
          textField="text"
          :readonly="item.isReadonly"
        >
        </ItpFormSelect2>
      </div>
      <div class="col-4">
        <ItpFormSelect2
          name="document_type"
          v-model="item.document_type"
          :value="item.document_type"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
              (item.document_type =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event);
            evh_1468474219929533_input($event, {});
          "
          :options="documentTypes"
          valueField="value"
          textField="text"
          :disabled="item.isReadonly"
        >
        </ItpFormSelect2>
      </div>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="3"
      :content-cols-md="9"
      name="subject"
      :label="pgettext('RegisterEntry', 'Subject')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormInput
        name="subject"
        v-model="item.subject"
        :value="item.subject"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.subject =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        required
        autofocus
        :disabled="item.isReadonly"
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="3"
      :content-cols-md="9"
      name="reference_number"
      :label="pgettext('RegisterEntry', 'Reference number')"
      :labelAlign="labelAlign"
      :helpTooltip="
        pgettext(
          'RegisterEntry|Help|Reference number',
          'The invoice number, contract number or other number identifying the registered document.'
        )
      "
    >
      <ItpFormInput
        name="reference_number"
        v-model="item.reference_number"
        :value="item.reference_number"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
            (item.reference_number =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event);
          evh_6917646200545441_input($event, {});
        "
        :readonly="item.isReadonly"
      >
      </ItpFormInput>
      <div
        class="alert alert-in-form alert-primary"
        v-if="invoiceCheckResult && invoiceCheckResult.register_entry == null"
      >
        <ItpIcon
          fa="info-circle"
          v-if="invoiceCheckResult.register_entry == null"
        >
        </ItpIcon>
        <span>
          {{
            pgettext(
              'RegisterEntry',
              'An invoice exists with the given number. Click to assign it to the current entry.'
            )
          }}
        </span>
        <ItpLink
          :text="pgettext('RegisterEntry', 'Assign invoice')"
          class="d-inline-block"
          @click.prevent.stop="
            evh_6941071772471093_clickPreventStop($event, {})
          "
        >
        </ItpLink>
      </div>
      <div
        class="alert alert-in-form alert-warning"
        v-if="invoiceCheckResult && invoiceCheckResult.register_entry != null"
      >
        <ItpIcon fa="exclamation-circle"> </ItpIcon>
        <span>
          {{
            pgettext(
              'RegisterEntry',
              "An invoice exists with the given number but it's already registered."
            )
          }}
        </span>
        <UcRegisterEntryMiniCard
          :item="invoiceCheckResult.register_entry"
          no-icon
        >
        </UcRegisterEntryMiniCard>
      </div>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="3"
      :content-cols-md="6"
      name="clerk"
      :label="pgettext('RegisterEntry', 'Clerk')"
      :labelAlign="labelAlign"
      :helpTooltip="
        pgettext(
          'RegisterEntry|Help|Clerk',
          'New values entered are memorized and saved to the database.'
        )
      "
    >
      <ItpFormSelect2
        name="clerk"
        taggable
        :options="clerks"
        v-model="item.clerk"
        :value="item.clerk"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.clerk =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :readonly="item.isReadonly"
        valueField="name"
        textField="name"
      >
      </ItpFormSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="3"
      :content-cols-md="9"
      name="client"
      :labelAlign="labelAlign"
      :label="pgettext('RegisterEntry', 'Partner')"
      :helpTooltip="
        pgettext(
          'RegisterEntry|Help|Partner',
          'Search and select a company or enter it\'s name if the database does not contain its data. Type ENTER after typing in a new value.'
        )
      "
    >
      <UcRegisterEntryPartnerSelector :editor="item">
      </UcRegisterEntryPartnerSelector>
    </ItpFormGroup>
    <hr />
    <ItpFormGroup
      :label-cols-md="3"
      :content-cols-md="9"
      name="invoice"
      :label="pgettext('RegisterEntry', 'Invoice')"
      :labelAlign="labelAlign"
      v-if="item.document_type == 'invoice'"
    >
      <UcRegisterEntryInvoiceSelector :editor="item">
      </UcRegisterEntryInvoiceSelector>
      <ItpButton
        :text="pgettext('RegisterEntry', 'Create invoice...')"
        size="sm"
        variant="primary"
        :tooltip="
          pgettext(
            'RegisterEntry',
            'Create invoice with the given reference number.'
          )
        "
        :icon="$config.addIcon"
        class="mt-2"
        v-if="
          item.id &&
          (!item.invoice || !item.invoice.id) &&
          item.reference_number
        "
        @click.prevent.stop="evh_7463771596263925_clickPreventStop($event, {})"
      >
      </ItpButton>
    </ItpFormGroup>
    <ItpFormGroup
      name="ref_entry"
      :label="pgettext('RegisterEntry', 'Reference entry')"
      :labelAlign="labelAlign"
      :label-cols-md="3"
      :content-cols-md="9"
      :helpTooltip="
        pgettext(
          'RegisterEntry|Help|Clerk',
          'Reference of an other entry in any version of the current register.'
        )
      "
    >
      <ItpSelect2
        name="ref_entry"
        v-model="item.ref_entry"
        :value="item.ref_entry"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.ref_entry =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :dataSource="b_8680084583475136_dataSource"
        :initialOptions="item.ref_entry ? [item.ref_entry] : []"
        valueField="id"
        clearable
        bind-object
        optionTemplate="<UcSuggestRegisterEntryOption :option=option></UcSuggestRegisterEntryOption>"
        use-option-template-for-selected-option
        :readonly="item.isReadonly"
        @optionSelected="evh_8680084583475136_optionSelected($event, {})"
      >
      </ItpSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="3"
      :content-cols-md="5"
      name="status"
      :label="pgettext('RegisterEntry', 'Status')"
      :labelAlign="labelAlign"
      :helpTooltip="
        pgettext(
          'RegisterEntry|Help|Status',
          'Type in arbitrary values to assign a status. The values are memorized. Press ENTER after typing in a new value.'
        )
      "
    >
      <ItpFormSelect2
        name="status"
        taggable
        :options="statuses"
        v-model="item.status"
        :value="item.status"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.status =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :readonly="item.isReadonly"
        valueField="name"
        textField="name"
      >
      </ItpFormSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="3"
      :content-cols-md="9"
      name="categories"
      :label="pgettext('RegisterEntry', 'Categories')"
      :labelAlign="labelAlign"
      :helpTooltip="
        pgettext(
          'RegisterEntry|Help|Categories',
          'Assign multiple arbitrary values. The values are memorized. Press ENTER after typing in a new value.'
        )
      "
    >
      <ItpSelect2
        name="categories"
        multiple
        taggable
        :options="categories"
        v-model="item.categories"
        :value="item.categories"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.categories =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :readonly="item.isReadonly"
        valueField="name"
        textField="name"
      >
      </ItpSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="3"
      :content-cols-md="9"
      name="project"
      :label="pgettext('RegisterEntry', 'Project')"
      :labelAlign="labelAlign"
    >
      <UcProjectSelector :item="item" name="project"> </UcProjectSelector>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="3"
      :content-cols-md="3"
      name="delivery_date"
      :label="pgettext('RegisterEntry', 'Delivery date')"
      :labelAlign="labelAlign"
      :helpTooltip="
        pgettext(
          'RegisterEntry|Help|Delivery date',
          'Receive or delivery date.'
        )
      "
    >
      <ItpFormDatetime
        name="delivery_date"
        v-model="item.delivery_date"
        :readonly="item.isReadonly"
      >
      </ItpFormDatetime>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="3"
      :content-cols-md="5"
      name="delivery_date"
      :label="pgettext('RegisterEntry', 'Delivery modes')"
      :labelAlign="labelAlign"
    >
      <ItpSelect2
        name="delivery_modes"
        v-model="item.delivery_modes"
        :value="item.delivery_modes"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.delivery_modes =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :options="deliveryModes"
        valueField="name"
        textField="name"
        multiple
      >
      </ItpSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="3"
      :content-cols-md="3"
      name="due_date"
      :label="pgettext('RegisterEntry', 'Due date')"
      :labelAlign="labelAlign"
    >
      <ItpFormDatetime
        name="due_date"
        v-model="item.due_date"
        :readonly="item.isReadonly"
      >
      </ItpFormDatetime>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="3"
      :content-cols-md="3"
      name="priority"
      :label="pgettext('RegisterEntry', 'Priority')"
      :labelAlign="labelAlign"
    >
      <ItpFormSelect2
        name="priority"
        v-model="item.priority"
        :value="item.priority"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.priority =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :options="priorities"
        :readonly="item.isReadonly"
      >
      </ItpFormSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="3"
      :content-cols-md="5"
      name="storage_location"
      :label="pgettext('RegisterEntry', 'Storage location')"
      :labelAlign="labelAlign"
      :helpTooltip="
        pgettext(
          'RegisterEntry|Help|Storage location',
          'The description or number of the document\'s storage location.'
        )
      "
    >
      <ItpFormInput
        name="storage_location"
        v-model="item.storage_location"
        :value="item.storage_location"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.storage_location =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :readonly="item.isReadonly"
      >
      </ItpFormInput>
    </ItpFormGroup>
    <hr />
    <ItpFormGroup
      :label-cols-md="3"
      :content-cols-md="8"
      name="notes"
      :label="pgettext('RegisterEntry', 'Notes')"
      :labelAlign="labelAlign"
    >
      <ItpFormTextarea
        name="notes"
        v-model="item.notes"
        :value="item.notes"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.notes =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :readonly="item.isReadonly"
      >
      </ItpFormTextarea>
    </ItpFormGroup>
    <hr v-if="item.id" />
    <UcAttachmentsList :parent="item" v-if="item.id"> </UcAttachmentsList>
  </ItpUsercontrol>
</template>
